import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from './use-auth';

import type { DiscoverContext } from '@/lib/discover/context-types';

export interface DiscoverStaleResponseGuard {
  reset(): void;
}

export interface UseDiscoverContextOptions {
  staleResponseGuard?: DiscoverStaleResponseGuard;
}

const DISCOVER_KEY = ['discover'] as const;

function discoverContextBootstrapQueryKey(actorId: string | undefined) {
  return ['discover-context-bootstrap', actorId ?? 'anonymous'] as const;
}

export function discoverContextQueryKey(
  actorId: string | undefined,
  context?: Pick<DiscoverContext, 'ownerId' | 'brandKitId' | 'revision'> | null,
) {
  return [
    'discover-context',
    actorId ?? 'anonymous',
    context?.ownerId ?? 'unresolved',
    context?.brandKitId ?? 'unresolved',
    context?.revision ?? 0,
  ] as const;
}

async function requestContext(token: string | null, brandKitId?: string): Promise<DiscoverContext> {
  const response = await fetch('/api/discover/context', {
    method: brandKitId ? 'POST' : 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(brandKitId ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(brandKitId ? { body: JSON.stringify({ brandKitId }) } : {}),
  });
  if (!response.ok) throw new Error(`Discover context request failed (${response.status})`);
  return (await response.json()) as DiscoverContext;
}

export function useDiscoverContext(options: UseDiscoverContextOptions = {}) {
  const { user, session } = useAuth();
  const token = session?.access_token ?? null;
  const queryClient = useQueryClient();
  const bootstrapKey = discoverContextBootstrapQueryKey(user?.id);
  const bootstrapQuery = useQuery({
    queryKey: bootstrapKey,
    queryFn: () => requestContext(token),
    enabled: Boolean(user?.id),
    staleTime: 60_000,
  });

  const resolvedKey = bootstrapQuery.data
    ? discoverContextQueryKey(user?.id, bootstrapQuery.data)
    : (['discover-context-pending', user?.id ?? 'anonymous'] as const);
  const scopedQuery = useQuery({
    queryKey: resolvedKey,
    queryFn: () => requestContext(token),
    enabled: Boolean(user?.id && bootstrapQuery.data),
    initialData: bootstrapQuery.data,
    initialDataUpdatedAt: bootstrapQuery.dataUpdatedAt,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (brandKitId: string) => requestContext(token, brandKitId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: DISCOVER_KEY });
      options.staleResponseGuard?.reset();
    },
    onSuccess: (context) => {
      queryClient.setQueryData(bootstrapKey, context);
      queryClient.setQueryData(discoverContextQueryKey(user?.id, context), context);
      void queryClient.invalidateQueries({ queryKey: DISCOVER_KEY });
    },
  });

  return {
    context: scopedQuery.data ?? bootstrapQuery.data ?? null,
    isLoading: bootstrapQuery.isLoading || scopedQuery.isLoading,
    error: bootstrapQuery.error ?? scopedQuery.error,
    selectBrandKit: mutation.mutateAsync,
    isSelectingBrandKit: mutation.isPending,
  };
}
